import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    fetchTournamentPlanningContext, fetchTournamentProposals, fetchMyTournamentProposals,
    createTournamentProposal, acceptTournamentProposal, cancelTournamentProposal,
    fetchTournamentSlotCourts, checkTournamentWindowAvailability,
    redirectToLoginIfInvalidToken
} from '../../api/api';
import Spinner from '../Spinner/Spinner';
import CustomModal from '../../UI/Modal/CustomModal';
import ProposalCard from './ProposalCard';
import { formatProposalDate } from './formatProposalDate';
import { arrangedOpponentIds } from './arrangedOpponents';
import styles from './TournamentMatchPlanning.module.css';

const extractErrorMessage = (data) => {
    if (!data) return 'Nieznany błąd';
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return JSON.stringify(data);
};

const TournamentMatchPlanning = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();

    const [context, setContext] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [myProposals, setMyProposals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isPositiveMessage, setIsPositiveMessage] = useState(false);
    const [pendingCancel, setPendingCancel] = useState(null);

    const [proposedDate, setProposedDate] = useState('');
    const [proposedFromTime, setProposedFromTime] = useState('');
    const [proposedToTime, setProposedToTime] = useState('');

    const [acceptSlider, setAcceptSlider] = useState(null); // { proposalId, proposedDate, windowStart, windowEnd }
    const [sliderIdx, setSliderIdx] = useState(0);
    const [slotCourts, setSlotCourts] = useState([]);
    const [sliderLoading, setSliderLoading] = useState(false);
    const lastFetchedSlotRef = useRef(null); // { proposalId, slotStart } — pomija re-fetch przy otwarciu slidera

    const showModal = (msg, positive = false) => {
        setModalMessage(msg);
        setIsPositiveMessage(positive);
    };

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const [ctx, all, mine] = await Promise.all([
            fetchTournamentPlanningContext(tournamentId),
            fetchTournamentProposals(tournamentId),
            fetchMyTournamentProposals(tournamentId),
        ]);
        if (ctx.success) {
            setContext(ctx.data);
            setLoadError(false);
        } else {
            // 200 z enabled=false pokrywa wszystkie legalne przypadki (nie-uczestnik, wyłączone
            // planowanie, nieznany turniej) — tu trafiamy tylko przy realnej awarii (5xx, sieć).
            setLoadError(true);
        }
        if (all.success) setProposals(all.data);
        if (mine.success) setMyProposals(mine.data);
        setIsLoading(false);
    }, [tournamentId]);

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate, `/tournaments/${tournamentId}/match-planning`);
    }, [navigate, tournamentId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Opcje "do": min fromTime+2h, max 22:00 co 30 min
    const toTimeOptions = useMemo(() => {
        if (!proposedFromTime) return [];
        const [fh, fm] = proposedFromTime.split(':').map(Number);
        const minMins = fh * 60 + fm + 120;
        const maxMins = 22 * 60;
        const opts = [];
        for (let mins = minMins; mins <= maxMins; mins += 30) {
            opts.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`);
        }
        return opts;
    }, [proposedFromTime]);

    // Opcje "od": 07:00 – 20:00 co 30 min
    const fromTimeOptions = [];
    for (let h = 7; h <= 20; h++) {
        fromTimeOptions.push(`${String(h).padStart(2, '0')}:00`);
        if (h < 20) fromTimeOptions.push(`${String(h).padStart(2, '0')}:30`);
    }

    // Sloty suwaka: wszystkie starty 2h slotów w oknie propozycji
    const slotStarts = useMemo(() => {
        if (!acceptSlider) return [];
        const [fh, fm] = acceptSlider.windowStart.split(':').map(Number);
        const [th, tm] = acceptSlider.windowEnd.split(':').map(Number);
        const windowMins = (th * 60 + tm) - (fh * 60 + fm);
        const count = Math.floor((windowMins - 120) / 30) + 1;
        return Array.from({ length: count }, (_, i) => {
            const mins = fh * 60 + fm + i * 30;
            return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`;
        });
    }, [acceptSlider]);

    const addTwoHours = (t) => {
        const [h, m] = t.split(':').map(Number);
        return `${String(h + 2).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
    };

    // Pobierz dostępne korty po zatrzymaniu suwaka (debounce 250ms).
    // Pomija fetch jeśli slot jest już załadowany (ref ustawiony przy otwarciu slidera).
    useEffect(() => {
        if (!acceptSlider || !slotStarts.length) return;
        const slotStart = slotStarts[sliderIdx];
        const ref = lastFetchedSlotRef.current;
        if (ref && ref.proposalId === acceptSlider.proposalId && ref.slotStart === slotStart) return;
        setSliderLoading(true);
        const timer = setTimeout(() => {
            lastFetchedSlotRef.current = { proposalId: acceptSlider.proposalId, slotStart };
            fetchTournamentSlotCourts(tournamentId, acceptSlider.proposalId, slotStart)
                .then(r => setSlotCourts(r.success ? r.data : []))
                .finally(() => setSliderLoading(false));
        }, 250);
        return () => clearTimeout(timer);
    }, [sliderIdx, acceptSlider, slotStarts, tournamentId]);

    const handleCreate = async () => {
        if (!proposedDate || !proposedFromTime || !proposedToTime) {
            showModal('Uzupełnij datę i godziny okna.');
            return;
        }
        const availability = await checkTournamentWindowAvailability(
            tournamentId, proposedDate, proposedFromTime, proposedToTime);
        if (availability.success && availability.data?.available === false) {
            showModal('W tym oknie nie ma wolnego kortu na pełne 2 godziny — wybierz inny termin.');
            return;
        }
        const result = await createTournamentProposal(
            tournamentId, proposedDate, proposedFromTime, proposedToTime);
        if (!result.success) {
            showModal(extractErrorMessage(result.data));
            return;
        }
        setProposedDate('');
        setProposedFromTime('');
        setProposedToTime('');
        await loadData();
    };

    const doAccept = async (proposalId, slots = null) => {
        const result = await acceptTournamentProposal(tournamentId, proposalId, false, slots);
        if (!result.success) {
            const msg = extractErrorMessage(result.data);
            if (msg === 'ALREADY_AGREED') {
                showModal('Ten mecz ma już umówiony termin. Anuluj poprzedni, aby ustalić nowy.');
            } else if (msg === 'COURT_TAKEN') {
                showModal('Ten kort właśnie zajął ktoś inny — wybierz inny.');
            } else {
                showModal(msg);
            }
            await loadData();
            return;
        }
        const court = result.data?.reservedCourtInfo;
        showModal(`Mecz zaakceptowany! Zarezerwowano ${court || 'kort'}. Sprawdź email.`, true);
        await loadData();
    };

    const handleAccept = async (proposal) => {
        const [fh, fm] = proposal.proposedStartTime.split(':').map(Number);
        const [th, tm] = proposal.proposedEndTime.split(':').map(Number);
        const windowMins = (th * 60 + tm) - (fh * 60 + fm);
        const count = Math.floor((windowMins - 120) / 30) + 1;
        const starts = Array.from({ length: count }, (_, i) => {
            const mins = fh * 60 + fm + i * 30;
            return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`;
        });

        const results = await Promise.all(
            starts.map(s => fetchTournamentSlotCourts(tournamentId, proposal.id, s))
        );
        const firstAvailableIdx = results.findIndex(r => r.success && r.data.length > 0);

        if (firstAvailableIdx === -1) {
            // Brak kortów — backend anuluje propozycję
            await doAccept(proposal.id, null);
            return;
        }

        // Preładuj korty dla pierwszego wolnego slotu, otwórz slider bez flashowania
        lastFetchedSlotRef.current = { proposalId: proposal.id, slotStart: starts[firstAvailableIdx] };
        setSliderIdx(firstAvailableIdx);
        setSlotCourts(results[firstAvailableIdx].data);
        setSliderLoading(false);
        setAcceptSlider({
            proposalId: proposal.id,
            proposedDate: proposal.proposedDate,
            windowStart: proposal.proposedStartTime,
            windowEnd: proposal.proposedEndTime,
        });
    };

    const handleSlotCourtPick = async (court) => {
        if (!acceptSlider || !slotStarts.length) return;
        const slotStart = slotStarts[sliderIdx];
        const slotEnd = addTwoHours(slotStart);
        const pid = acceptSlider.proposalId;
        setAcceptSlider(null);
        await doAccept(pid, [{ courtId: court.courtId, courtName: court.courtName, startTime: slotStart, endTime: slotEnd }]);
    };

    const doCancel = async (proposal) => {
        setPendingCancel(null);
        const result = await cancelTournamentProposal(tournamentId, proposal.id, null);
        if (!result.success) {
            showModal(extractErrorMessage(result.data));
        }
        // Przeładowanie także po błędzie — konflikt zwykle znaczy, że ktoś zmienił stan równolegle.
        await loadData();
    };

    // Anulowanie ACCEPTED kasuje rezerwację kortu i wysyła maile obu graczom — wymaga
    // potwierdzenia. Anulowanie ACTIVE jest nieszkodliwe (znika tylko propozycja terminu).
    const handleCancel = (proposal) => {
        if (proposal.status === 'ACCEPTED') {
            setPendingCancel(proposal);
            return;
        }
        doCancel(proposal);
    };

    if (isLoading) return <Spinner />;

    if (loadError || !context) {
        return (
            <div className={styles.container}>
                <div className={styles.headerRow}>
                    <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: Number(tournamentId) } })}>← Powrót</button>
                </div>
                <p className={styles.frozenBanner}>
                    Nie udało się wczytać planowania meczów. Odśwież stronę albo spróbuj później.
                </p>
            </div>
        );
    }

    if (!context.enabled || !context.myParticipantId) {
        return (
            <div className={styles.container}>
                <div className={styles.headerRow}>
                    <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: Number(tournamentId) } })}>← Powrót</button>
                </div>
                <p className={styles.frozenBanner}>
                    Planowanie meczów nie jest dostępne w tym turnieju.
                </p>
            </div>
        );
    }

    const isKnockout = context.phase === 'KNOCKOUT';
    const phaseLine = isKnockout
        ? `Faza pucharowa${context.opponentName ? ` — rywal: ${context.opponentName}` : ''}`
        : `Faza grupowa — grupa ${context.myGroupLabel}`;

    // Rywale, z którymi termin już stoi — liczone z obu list, bo umowa może wisieć zarówno na
    // ofercie cudzej (ja akceptowałem), jak i na mojej (ktoś przyjął moją).
    const arrangedIds = arrangedOpponentIds(
        [...proposals, ...myProposals], context.myParticipantId);

    const canAcceptProposal = (p) =>
        p.status === 'ACTIVE'
        && p.proposerParticipantId !== context.myParticipantId
        && (context.eligibleOpponentIds || []).includes(p.proposerParticipantId)
        && !arrangedIds.has(p.proposerParticipantId);

    // Backend pozwala anulować proponującemu ORAZ akceptującemu — front odwzorowuje dokładnie tę regułę.
    const canCancelProposal = (p) =>
        (p.status === 'ACTIVE' || p.status === 'ACCEPTED')
        && (p.proposerParticipantId === context.myParticipantId
            || p.accepterParticipantId === context.myParticipantId);

    const blockedHint = isKnockout
        ? 'To nie jest Twój rywal w tej rundzie'
        : 'Rozegrałeś już z tym graczem mecz';

    return (
        <div className={styles.container}>
            <CustomModal
                isOpen={!!modalMessage}
                message={modalMessage}
                isPositiveMessage={isPositiveMessage}
                onRequestClose={() => setModalMessage('')}
                cancelLabel="Zamknij"
                showConfirmButton={false}
            />

            <CustomModal
                isOpen={!!pendingCancel}
                message="Anulować umówiony mecz? Rezerwacja kortu zostanie zwolniona, a rywal dostanie maila."
                onRequestClose={() => setPendingCancel(null)}
                isWarningMessage={true}
                confirmLabel="Anuluj mecz"
                onConfirm={() => doCancel(pendingCancel)}
                cancelLabel="Zostaw"
                showConfirmButton={true}
            />

            <div className={styles.headerRow}>
                <button className={styles.backButton} onClick={() => navigate('/tournaments', { state: { activeTab: 'current', tournamentId: Number(tournamentId) } })}>← Powrót</button>
            </div>

            <h2 className={styles.title}>Planowanie meczów</h2>
            <p className={styles.phaseLine}>{phaseLine}</p>

            {!context.canPropose && (
                <div className={styles.frozenBanner}>{context.blockReason}</div>
            )}

            {context.canPropose && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Zaproponuj termin</h3>
                    <div className={styles.formStack}>
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>Data</label>
                            <input
                                className={styles.formInput}
                                type="date"
                                value={proposedDate}
                                onChange={(e) => setProposedDate(e.target.value)}
                                min={format(new Date(), 'yyyy-MM-dd')}
                            />
                        </div>
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>Od</label>
                            <select className={styles.formInput} value={proposedFromTime} onChange={(e) => { setProposedFromTime(e.target.value); setProposedToTime(''); }}>
                                <option value="">— wybierz —</option>
                                {fromTimeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>Do</label>
                            <select className={styles.formInput} value={proposedToTime} onChange={(e) => setProposedToTime(e.target.value)} disabled={!proposedFromTime}>
                                <option value="">— wybierz —</option>
                                {toTimeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className={styles.formButtons}>
                            <button
                                className={styles.addButton}
                                onClick={handleCreate}
                                disabled={!proposedDate || !proposedFromTime || !proposedToTime}
                            >
                                Dodaj propozycję
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Propozycje terminów</h3>
                {proposals.filter(p => p.proposerParticipantId !== context.myParticipantId).length === 0 ? (
                    <p>Brak ofert.</p>
                ) : (
                    proposals
                        .filter(p => p.proposerParticipantId !== context.myParticipantId)
                        .map(p => (
                            <ProposalCard
                                key={p.id}
                                proposal={p}
                                canAccept={canAcceptProposal(p)}
                                canCancel={canCancelProposal(p)}
                                alreadyArranged={arrangedIds.has(p.proposerParticipantId)}
                                blockedHint={blockedHint}
                                onAccept={handleAccept}
                                onCancel={handleCancel}
                            />
                        ))
                )}
            </div>

            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Moje propozycje</h3>
                {myProposals.length === 0 ? (
                    <p>Nie masz wystawionych ofert.</p>
                ) : (
                    myProposals.map(p => (
                        <ProposalCard
                            key={p.id}
                            proposal={p}
                            isMine={true}
                            canAccept={false}
                            canCancel={canCancelProposal(p)}
                            onAccept={() => {}}
                            onCancel={handleCancel}
                        />
                    ))
                )}
            </div>

            {acceptSlider && (
                <div className={styles.sliderModalOverlay} onClick={() => setAcceptSlider(null)}>
                    <div className={styles.sliderModalContent} onClick={e => e.stopPropagation()}>
                        <p className={styles.sliderDate}>{formatProposalDate(acceptSlider.proposedDate)}</p>
                        <h3 className={styles.sliderTitle}>Wybierz godzinę i kort</h3>
                        <p className={styles.sliderLabel}>
                            {slotStarts[sliderIdx]} – {addTwoHours(slotStarts[sliderIdx])}
                        </p>
                        {slotStarts.length > 1 && (
                            <input
                                type="range"
                                min={0}
                                max={slotStarts.length - 1}
                                step={1}
                                value={sliderIdx}
                                onChange={e => setSliderIdx(Number(e.target.value))}
                                className={styles.sliderInput}
                            />
                        )}
                        {slotCourts.length === 0 && !sliderLoading ? (
                            <p className={styles.noCourts}>Brak wolnych kortów o tej godzinie</p>
                        ) : slotCourts.length > 0 ? (
                            <div className={`${styles.courtPickButtons} ${sliderLoading ? styles.courtPickButtonsLoading : ''}`}>
                                {slotCourts.map(c => (
                                    <button key={c.courtId} className={styles.courtPickButton}
                                            onClick={() => handleSlotCourtPick(c)}>
                                        {c.courtName}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        <div className={styles.cancelModalButtons}>
                            <button className={styles.cancelButton} onClick={() => setAcceptSlider(null)}>Anuluj</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TournamentMatchPlanning;
